// ============================================================================
// Sourcing data hooks — single source of truth for the Sourcing module
// (pipeline, discovery, signal loop, referral codes). 2026-07-02.
//
// TanStack-Query-shaped API over plain useState/useEffect, mirroring
// hr-hiring/hooks/use-hiring-data.ts (the project has no react-query dep).
//
// RLS does all scoping: can_access_sourcing() gates every sourcing table and
// the sourcing_signal view, so no client-side access checks are needed here —
// a non-allowlisted user simply gets zero rows (and never sees the routes).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { getSupabaseToken, supabase } from '../../../shared/supabase';
import { rpcErrorMessage } from '../../../shared/rpc-error';
import { alreadyOnFileFrom } from '../../subcontractors/already-on-file';

// ---------------------------------------------------------------------------
// Types (match live schema; regen db types covers the generated side)
// ---------------------------------------------------------------------------

// The pipeline's stage words and their labels moved to src/modules/subcontractors/stages.ts
// when the pipeline became the sub's own status (item #683, TCSD-0368). The application
// detail (intake answers, fit score, talk track, referral source) still lives in
// sourcing_prospects, keyed to the sub by promoted_provider_id.

export interface SourcingSource {
  source_id: string;
  name: string;
  category: string;
  market_coverage: string[];
  access_type: string;
  cost_note: string | null;
  tos_scrape_allowed: boolean;
  enabled: boolean;
  notes: string | null;
}

export type LeadEnrichmentStatus = 'none' | 'queued' | 'enriched' | 'no_match' | 'error';

export interface DiscoveredLead {
  lead_id: string;
  source_id: string | null;
  business_name: string;
  owner_name: string | null;
  market: string | null;
  phone: string | null;
  email: string | null;
  raw_json: Record<string, unknown> | null;
  imported_by: string | null;
  imported_at: string;
  triaged_to_prospect_id: string | null;
  status: 'New' | 'Triaged' | 'Discarded';
  // Enrichment (added 2026-07-09)
  website: string | null;
  enriched_email: string | null;
  enriched_phone: string | null;
  years_in_business: number | null;
  has_insurance: boolean | null;
  medical_experience: boolean | null;
  services: string[] | null;
  subcontracts: boolean | null;
  enrichment_summary: string | null;
  enriched_at: string | null;
  enrichment_source: string | null;
  enrichment_status: LeadEnrichmentStatus;
  // Generated columns
  ppp_jobs: number | null;
  entity_type: string | null;
  city_name: string | null;
  is_company_name: boolean | null;
}

// Fixed option list for the discovery filter bar. Entity types are the stable
// set produced by the PPP dataset (fetching distinct would hit PostgREST's
// 1000-row cap). The market filter is NOT here: it reads the master market list
// (useMarkets), because a typed copy drifted and matched no stored lead (item 1052).
export const DISCOVERY_ENTITY_TYPES = [
  'Corporation',
  'Subchapter S Corporation',
  'Limited  Liability Company(LLC)',
  'Single Member LLC',
  'Sole Proprietorship',
  'Partnership',
  'Limited Liability Partnership',
  'Self-Employed Individuals',
  'Independent Contractors',
  'Non-Profit Organization',
  'Professional Association',
  'Cooperative',
  'Joint Venture',
  'Qualified Joint-Venture (spouses)',
] as const;

export type JobsBucket = '' | '1' | '2-4' | '5-9' | '10+';
export type LeadNameKind = '' | 'company' | 'personal';
export type LeadContactFilter = '' | 'has';

export interface DiscoveryFilters {
  search: string;
  market: string;
  jobsBucket: JobsBucket;
  entityType: string;
  source: string;
  nameKind: LeadNameKind;
  contact: LeadContactFilter;
  enrichmentStatus: '' | LeadEnrichmentStatus;
}

export interface ReferralCode {
  referral_code_id: string;
  code: string;
  kind: 'QR' | 'Referral';
  label: string | null;
  provider_id: string | null;
  worker_name: string | null;
  active: boolean;
  created_by: string | null;
  created_at: string;
}

export interface SignalRow {
  provider_id: string;
  worker_name: string | null; // null = provider-level rollup
  positive_count: number;
  neutral_count: number;
  negative_count: number;
  critical_count: number;
  sample_size: number;
  signal_score: number;
  confidence: 'low' | 'med' | 'high';
  last_feedback_date: string | null;
}

/** Public intake link. Takes either an open referral code or a per-prospect
 *  token — /apply/:token resolves both through sourcing_intake_context. */
export function intakeUrl(code: string): string {
  return `${window.location.origin}/apply/${code}`;
}

/**
 * Mint a one-prospect application link and return its URL.
 *
 * ⛔ This door existed in the database from 2026-09-09 and NOTHING IN THE APP
 * CALLED IT: sourcing_intake_tokens had zero rows, so the per-prospect link a
 * prior run reported as delivered could not be obtained by anybody. The open
 * referral codes are a different thing — one code printed on a flyer, shared by
 * everyone — and they never prefill a named prospect.
 *
 * The token carries the prospect, so the form opens with their name, phone,
 * email and market already filled and the application comes back attached to
 * the right row. It is single-use and expires 30 days after minting; minting
 * again simply issues another.
 */
export async function mintProspectIntakeUrl(prospectId: string): Promise<string> {
  const { data, error: err } = await supabase.rpc('mint_sourcing_intake_token', {
    p_prospect_id: prospectId,
  });
  if (err) throw new Error(rpcErrorMessage(err, 'Could not create an application link.'));
  const token = String((data as Record<string, unknown>)?.token ?? '');
  if (!token) throw new Error('The application link came back empty.');
  return intakeUrl(token);
}

// ---------------------------------------------------------------------------
// Edge function — sourcing-ai (fit score + talk track)
// ---------------------------------------------------------------------------

const SOURCING_AI_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sourcing-ai`;

export async function callSourcingAi(
  action: 'fit_score' | 'talk_track',
  prospectId: string,
): Promise<Record<string, unknown>> {
  const token = getSupabaseToken();
  if (!token) throw new Error('Not signed in.');
  const res = await fetch(SOURCING_AI_URL, {
    method: 'POST',
    // Authorization and Content-Type only — the headers sourcing-ai's CORS allows,
    // as the app calls facility-summary. Sending apikey too made the browser refuse
    // the call at its preflight, so this function had never been reached from the
    // app (found 2026-10-14 turning the fit score on, TCSD-0494).
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action, prospect_id: prospectId }),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new Error(String(body.error ?? `sourcing-ai failed (${res.status})`));
  }
  return body;
}

// ---------------------------------------------------------------------------
// Signal loop (learning read model)
// ---------------------------------------------------------------------------

export function useSourcingSignal() {
  const [data, setData] = useState<SignalRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    const { data: rows, error: err } = await supabase
      .from('sourcing_signal')
      .select('*')
      .order('sample_size', { ascending: false });
    if (err) setError(rpcErrorMessage(err, 'Sourcing request failed.'));
    else {
      setError(null);
      setData((rows ?? []) as unknown as SignalRow[]);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, isLoading, error, refetch };
}

/** Provider-level signal lookup map keyed by provider_id (worker rows excluded). */
export function providerSignalMap(rows: SignalRow[]): Map<string, SignalRow> {
  const m = new Map<string, SignalRow>();
  for (const r of rows) if (r.worker_name === null) m.set(r.provider_id, r);
  return m;
}

// ---------------------------------------------------------------------------
// Discovery (sources catalog + raw leads + triage)
// ---------------------------------------------------------------------------

const DISCOVERY_PAGE_SIZE = 50;

const DEFAULT_DISCOVERY_FILTERS: DiscoveryFilters = {
  search: '',
  market: '',
  jobsBucket: '',
  entityType: '',
  source: '',
  nameKind: '',
  contact: '',
  enrichmentStatus: '',
};

export function useDiscovery() {
  const [sources, setSources] = useState<SourcingSource[]>([]);
  const [leads, setLeads] = useState<DiscoveredLead[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<DiscoveryFilters>(DEFAULT_DISCOVERY_FILTERS);
  const [page, setPage] = useState(0);
  const pageSize = DISCOVERY_PAGE_SIZE;

  // Sources catalog — fetched once (small, stable).
  useEffect(() => {
    void (async () => {
      const { data, error: err } = await supabase
        .from('sourcing_sources')
        .select('*')
        .order('category')
        .order('name');
      if (!err) setSources((data ?? []) as unknown as SourcingSource[]);
    })();
  }, []);

  // Server-side filtered + paginated leads. PostgREST caps at 1000 rows, so
  // fetch-all is broken for 12k+ leads — every filter runs in the query.
  const refetch = useCallback(async () => {
    setIsLoading(true);
    let q = supabase
      .from('sourcing_discovered_leads')
      .select('*', { count: 'exact' })
      .eq('status', 'New');

    if (filters.market) q = q.eq('market', filters.market);
    if (filters.entityType) q = q.eq('entity_type', filters.entityType);
    if (filters.source) q = q.eq('source_id', filters.source);
    if (filters.nameKind === 'company') q = q.eq('is_company_name', true);
    else if (filters.nameKind === 'personal') q = q.eq('is_company_name', false);
    if (filters.contact === 'has') {
      q = q.or('phone.not.is.null,enriched_phone.not.is.null,enriched_email.not.is.null');
    }
    if (filters.enrichmentStatus) q = q.eq('enrichment_status', filters.enrichmentStatus);
    switch (filters.jobsBucket) {
      case '1':
        q = q.eq('ppp_jobs', 1);
        break;
      case '2-4':
        q = q.gte('ppp_jobs', 2).lte('ppp_jobs', 4);
        break;
      case '5-9':
        q = q.gte('ppp_jobs', 5).lte('ppp_jobs', 9);
        break;
      case '10+':
        q = q.gte('ppp_jobs', 10);
        break;
      default:
        break;
    }
    const s = filters.search.trim();
    if (s) {
      // Strip PostgREST filter metacharacters so the .or() expression can't break.
      const esc = s.replace(/[%,()]/g, ' ').trim();
      if (esc) q = q.or(`business_name.ilike.%${esc}%,city_name.ilike.%${esc}%`);
    }

    const { data, error: err, count } = await q
      .order('imported_at', { ascending: false })
      .range(page * pageSize, page * pageSize + pageSize - 1);

    if (err) setError(rpcErrorMessage(err, 'Sourcing request failed.'));
    else {
      setError(null);
      setLeads((data ?? []) as unknown as DiscoveredLead[]);
      setTotalCount(count ?? 0);
    }
    setIsLoading(false);
  }, [filters, page, pageSize]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const setFilter = useCallback(
    <K extends keyof DiscoveryFilters>(key: K, val: DiscoveryFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: val }));
      setPage(0);
    },
    [],
  );

  /** Manual entry or one row of a CSV import. */
  const addLeads = useCallback(
    async (rows: Array<Partial<DiscoveredLead>>, importedBy: string | null) => {
      if (rows.length === 0) return;
      const { error: err } = await supabase.from('sourcing_discovered_leads').insert(
        rows.map((r) => ({ ...r, imported_by: importedBy })),
      );
      if (err) throw new Error(rpcErrorMessage(err, 'Sourcing request failed.'));
      await refetch();
    },
    [refetch],
  );

  /** Promote a raw lead into the pipeline: a Lead through the one door (item #683). */
  const triageLead = useCallback(
    async (lead: DiscoveredLead, marketName: string | null, addAnyway = false) => {
      const { data, error: insErr } = await supabase.rpc('add_subcontractor', {
        p_name: lead.business_name || lead.owner_name || 'Unnamed lead',
        p_phone: lead.phone ?? lead.enriched_phone ?? '',
        p_coverage_markets: marketName ? [marketName] : [],
        p_capabilities: ['RECURRING'],
        p_status: 'Lead',
        p_email: lead.email ?? lead.enriched_email ?? undefined,
        p_channel: 'Discovery',
        p_discovery_source_id: lead.source_id ?? undefined,
        p_owner_name: lead.owner_name ?? undefined,
        p_add_anyway: addAnyway,
      });
      if (insErr) {
        const found = alreadyOnFileFrom(insErr);
        if (found) throw found;
        throw new Error(rpcErrorMessage(insErr, 'Sourcing request failed.'));
      }
      const prospectId = String((data as Record<string, unknown> | null)?.prospect_id ?? '');
      if (!prospectId) throw new Error('The new lead came back without an id.');
      const { error: updErr } = await supabase
        .from('sourcing_discovered_leads')
        .update({ status: 'Triaged', triaged_to_prospect_id: prospectId })
        .eq('lead_id', lead.lead_id);
      if (updErr) throw new Error(rpcErrorMessage(updErr, 'Sourcing request failed.'));
      await refetch();
      return prospectId;
    },
    [refetch],
  );

  const discardLead = useCallback(
    async (leadId: string) => {
      const { error: err } = await supabase
        .from('sourcing_discovered_leads')
        .update({ status: 'Discarded' })
        .eq('lead_id', leadId);
      if (err) throw new Error(rpcErrorMessage(err, 'Sourcing request failed.'));
      await refetch();
    },
    [refetch],
  );

  /** Dig deeper — queue a lead for enrichment (picked up by the batch pipeline). */
  const enrichLead = useCallback(
    async (leadId: string) => {
      const { error: err } = await supabase
        .from('sourcing_discovered_leads')
        .update({ enrichment_status: 'queued' })
        .eq('lead_id', leadId);
      if (err) throw new Error(rpcErrorMessage(err, 'Sourcing request failed.'));
      await refetch();
    },
    [refetch],
  );

  return {
    sources,
    leads,
    totalCount,
    isLoading,
    error,
    filters,
    setFilter,
    page,
    setPage,
    pageSize,
    refetch,
    addLeads,
    triageLead,
    discardLead,
    enrichLead,
  };
}

export type UseDiscovery = ReturnType<typeof useDiscovery>;

// ---------------------------------------------------------------------------
// Provider picker rows (for attributing codes to an existing cleaner company)
// ---------------------------------------------------------------------------

export interface ProviderOption {
  provider_id: string;
  partner_name: string;
  coverage_markets: string[] | null;
}

export function useProviderOptions() {
  const [data, setData] = useState<ProviderOption[]>([]);
  useEffect(() => {
    void (async () => {
      const { data: rows } = await supabase
        .from('service_providers')
        .select('provider_id, partner_name, coverage_markets')
        .in('provider_status', ['Active', 'Ready', 'Pending', 'Onboarding'])
        .order('partner_name');
      setData((rows ?? []) as unknown as ProviderOption[]);
    })();
  }, []);
  return data;
}
